/**
 * ActorSourcePopover
 *
 * Shows actor's full JTBD profile in a popover when clicking on a source reference.
 * Highlights the specific insight item that was aggregated.
 * Loads actor data from Firestore on demand with caching.
 */

import React, { useState, useEffect } from 'react';
import {
  Popover,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { ActorService } from '@modules/firebase-core/services/ActorService';
import type { Actor } from '@segmently/schema-models/entities';

// ── Actor cache (shared across popover instances) ────────────────────

const actorCache = new Map<string, Actor>();

const CATEGORY_LABELS: Record<string, string> = {
  coreJobs: 'Core Jobs',
  contexts: 'Contexts',
  fears: 'Fears',
  bigJobs: 'Big Jobs',
  subtasks: 'Subtasks',
  competitors: 'Competitors',
  habits: 'Habits',
};

const JTBD_KEYS = ['coreJobs', 'contexts', 'fears', 'bigJobs', 'subtasks', 'competitors', 'habits'];

// ── Types ────────────────────────────────────────────────────────────

interface ActorSourcePopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  source: {
    actorId: string;
    actorName: string;
    audienceId: string;
    originalItemId: string;
    originalDescription: string;
    originalWeight: number;
    similarityScore?: number;
  };
  category: string;
  projectId: string;
}

// ── Component ────────────────────────────────────────────────────────

export const ActorSourcePopover: React.FC<ActorSourcePopoverProps> = ({
  anchorEl,
  open,
  onClose,
  source,
  category,
  projectId,
}) => {
  const [actor, setActor] = useState<Actor | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const firestorePath = `projects/${projectId}/audiences/${source.audienceId}/actors/${source.actorId}/${category}/${source.originalItemId}`;

  useEffect(() => {
    if (!open || !source.actorId) return;

    // Check cache
    const cached = actorCache.get(source.actorId);
    if (cached) {
      setActor(cached);
      return;
    }

    // Load from Firestore
    setLoading(true);
    const service = new ActorService(projectId, source.audienceId);
    service.getWithSubcollections(source.actorId)
      .then((data) => {
        if (data) {
          actorCache.set(source.actorId, data);
          setActor(data);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open, source.actorId, source.audienceId, projectId]);

  const handleCopyPath = () => {
    navigator.clipboard.writeText(firestorePath);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { maxWidth: 480, maxHeight: 500, overflow: 'auto', p: 2 } } }}
    >
      {loading ? (
        <Box display="flex" alignItems="center" gap={1} p={2}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading actor...</Typography>
        </Box>
      ) : (
        <>
          {/* Header */}
          <Typography variant="subtitle1" fontWeight={600}>{source.actorName}</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Weight: {actor?.weight ?? '?'} | Audience: {source.audienceId.slice(0, 8)}...
          </Typography>

          <Divider sx={{ my: 1 }} />

          {/* Highlighted source insight */}
          <Box sx={{ bgcolor: 'action.selected', borderRadius: 1, p: 1.5, mb: 1.5 }}>
            <Typography variant="caption" color="primary" fontWeight={600}>
              Source insight ({CATEGORY_LABELS[category] || category})
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              &ldquo;{source.originalDescription}&rdquo;
            </Typography>
            <Stack direction="row" spacing={1} mt={0.5}>
              <Chip label={`w:${source.originalWeight}`} size="small" />
              {source.similarityScore != null && (
                <Chip label={`sim:${source.similarityScore}`} size="small" variant="outlined" />
              )}
            </Stack>
          </Box>

          {/* All JTBD categories */}
          {actor && JTBD_KEYS.map((key) => {
            const items = (actor as any)[key] || [];
            if (items.length === 0) return null;
            return (
              <Accordion key={key} disableGutters sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 32, py: 0 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {CATEGORY_LABELS[key]} ({items.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {items.map((item: any) => (
                    <Box
                      key={item.id}
                      sx={{
                        py: 0.5,
                        px: 1,
                        borderRadius: 0.5,
                        mb: 0.5,
                        bgcolor: item.id === source.originalItemId && key === category
                          ? 'primary.50'
                          : 'transparent',
                        border: item.id === source.originalItemId && key === category
                          ? '1px solid'
                          : 'none',
                        borderColor: 'primary.main',
                      }}
                    >
                      <Typography variant="body2" fontSize="0.8rem">
                        {item.description}
                        <Chip label={`w:${item.weight}`} size="small" sx={{ ml: 1 }} />
                      </Typography>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })}

          <Divider sx={{ my: 1 }} />

          {/* Firestore path */}
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.7rem' }}>
              {firestorePath}
            </Typography>
            <Tooltip title={copied ? 'Copied!' : 'Copy path'}>
              <IconButton size="small" onClick={handleCopyPath}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}
    </Popover>
  );
};
